/**
 * "Usar na mesa" (docs/spec/desk-cloud.md): só o que o RPG Desk já faz. Abrir como cena nova, usar como
 * fundo da cena aberta, pôr como token, tocar para a mesa, pôr na playlist e mandar no chat.
 *
 * De fora da mesa (a página do Desk Cloud), a ação fica pendente na store e a mesa abre; quando ela
 * carrega, quem sabe fazer pega a ação: o chat, a música e a cena nova aqui (`usePendingFileUse`, na
 * mesa); o fundo e o token, a cena aberta (SceneModule).
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { api } from '../api.js';
import { translate, useLanguage } from '../i18n/index.js';
import type { DeskFile } from '../shared.js';
import { useApp } from '../store.js';
import { useLoggedNav } from '../states/nav.js';
import { toast } from '../ui/Toaster.js';
import { setRooms } from './cloudActions.js';
import { GM_ACTIONS, splitName, type UseAction } from './cloudModel.js';

const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

/** O fundo de cada cena aberta na tela (o selo "Na cena" do painel Arquivos); a cena conta o dela. */
export const useSceneBackgrounds = create<{ byScene: Record<string, string | null>; report: (itemId: string, fileId: string | null) => void; drop: (itemId: string) => void }>()((set) => ({
  byScene: {},
  report: (itemId, fileId) => set((state) => (state.byScene[itemId] === fileId ? state : { byScene: { ...state.byScene, [itemId]: fileId } })),
  drop: (itemId) =>
    set((state) => {
      const { [itemId]: _gone, ...rest } = state.byScene;
      return { byScene: rest };
    }),
}));

/** Quanto a ação espera a mesa (e a cena) abrir antes de desistir com um aviso. */
const PENDING_MS = 15_000;

let nonce = 0;

/** O nome de cena, de token e de faixa que o arquivo dá: sem a extensão. */
export const nameOf = (name: string) => splitName(name).stem.slice(0, 120) || name;

/**
 * Usa o arquivo na mesa. As ações de mestre ligam o arquivo à mesa antes, se ele ainda não está (os
 * jogadores precisam abrir); mandar no chat liga no servidor. A mesa abre, se não está na tela.
 */
export async function applyInRoom(file: DeskFile, action: UseAction, roomId: string): Promise<void> {
  const app = useApp.getState();
  if (!app.token) return;
  if (GM_ACTIONS.has(action) && !file.rooms.includes(roomId) && file.ownerId === app.user?.id) {
    if (!(await setRooms([file], [roomId], []))) return;
    const name = app.rooms.find((room) => room.id === roomId)?.name ?? '';
    toast(say('cloud.use.linked', { room: name }), 'info');
  }
  if (file.ownerId === app.user?.id) void api.fileUsed(app.token, file.id).catch(() => undefined);
  app.setPendingFileUse({ nonce: ++nonce, roomId, fileId: file.id, name: file.name, action });
  if (app.activeRoomId === roomId) useLoggedNav.getState().go({ kind: 'room', roomId });
  else app.joinRoom(roomId);
}

/** O aviso de que deu certo. */
export function announceUse(action: UseAction, name: string): void {
  toast(say(`cloud.use.done.${action}`, { name: nameOf(name) }), 'success');
}

/**
 * Na mesa: pega a ação pendente desta mesa quando é do chat, da música ou da cena nova. A cena nova
 * passa a ação adiante, como "fundo" dela, depois de criada e aberta. A ação que ninguém pega (sem
 * cena aberta, sem permissão) some com um aviso.
 */
export function usePendingFileUse(): void {
  const pending = useApp((s) => s.pendingFileUse);
  const roomId = useApp((s) => s.room?.id ?? null);
  const library = useApp((s) => s.library);
  const playlist = useApp((s) => s.audio?.playlist);

  useEffect(() => {
    if (!pending || pending.roomId !== roomId) return;
    const app = useApp.getState();
    const url = `file:${pending.fileId}`;
    switch (pending.action) {
      case 'chat':
        if (app.takePendingFileUse(pending.nonce)) {
          app.attachStoredFile(pending.fileId);
          announceUse('chat', pending.name);
        }
        return;
      case 'playlist':
      case 'play': {
        const track = playlist?.find((item) => item.url === url);
        if (track) {
          if (!app.takePendingFileUse(pending.nonce)) return;
          if (pending.action === 'play') app.controlAudio('select', { trackId: track.id });
          announceUse(pending.action, pending.name);
          return;
        }
        if (!pending.enqueued) {
          app.setPendingFileUse({ ...pending, enqueued: true });
          app.enqueueAudio(url, nameOf(pending.name));
        }
        return;
      }
      case 'scene': {
        const name = nameOf(pending.name).slice(0, 80);
        if (!pending.knownScenes) {
          app.setPendingFileUse({ ...pending, knownScenes: library.filter((item) => item.kind === 'scene').map((item) => item.id) });
          app.createLibraryItem({ kind: 'scene', name });
          return;
        }
        const known = new Set(pending.knownScenes);
        const created = library.find((item) => item.kind === 'scene' && !known.has(item.id) && item.name === name);
        if (!created) return;
        // a cena nova faz o fundo, quando abrir (SceneModule)
        app.setPendingFileUse({ ...pending, action: 'background', itemId: created.id });
        app.requestOpenSheet(created.id);
        return;
      }
      default:
        // o fundo e o token: a cena aberta pega (SceneModule)
        return;
    }
  }, [pending, roomId, library, playlist]);

  const waiting = pending?.nonce;
  useEffect(() => {
    if (waiting === undefined) return;
    const timer = window.setTimeout(() => {
      const left = useApp.getState().pendingFileUse;
      if (!left || left.nonce !== waiting || !useApp.getState().takePendingFileUse(waiting)) return;
      toast(say(left.action === 'background' || left.action === 'token' ? 'cloud.use.noScene' : 'cloud.use.failed'), 'error');
    }, PENDING_MS);
    return () => window.clearTimeout(timer);
  }, [waiting]);
}
